import React from 'react';
import { Link } from 'react-router-dom';
import SeoHead from '@/components/layout/SeoHead';
import { ArrowLeft, Check } from 'lucide-react';
import { BRAND } from '@/data/site';
import { linkify } from '@/lib/linkify';

const LAST_UPDATED = 'May 8, 2026';

const SECTIONS = [
  {
    title: '1. Who We Are',
    blocks: [
      {
        type: 'p',
        text:
          'Ohana Property Services is a trade name of Ohana Property & Transition Services LLC, a registered Indiana limited liability company. We provide property cleanout, junk removal, estate cleanout, debris removal, and donation-conscious item recovery services across Central Indiana.',
      },
      { type: 'p', text: 'Contact:' },
      {
        type: 'list',
        items: [
          'Email: ohanas@ohanapropertyexperts.com',
          'Phone: (812) 302-6833',
          'Service Area: Columbus, Indianapolis, Lafayette, Bloomington, Greenwood, Franklin, Seymour, and surrounding Central Indiana communities.',
        ],
      },
    ],
  },
  {
    title: '2. Information We Collect',
    blocks: [
      {
        type: 'p',
        text:
          'When you submit a quote request or contact us through our website, we collect the following information:',
      },
      {
        type: 'list',
        items: [
          'Full name',
          'Phone number',
          'Email address',
          'Property address where service is requested',
          'Job type and service description',
          'Photos of the property submitted for quote purposes',
          'Preferred timeline or urgency',
          'Hazmat acknowledgment (yes or no)',
          'Terms of Service agreement (yes or no)',
          'Any additional notes or details you provide',
        ],
      },
      {
        type: 'p',
        text:
          'We collect this information solely to provide property cleanout and related services and to communicate with you about your job request.',
      },
    ],
  },
  {
    title: '3. How We Use Your Information',
    blocks: [
      { type: 'p', text: 'We use the information you provide to:' },
      {
        type: 'list',
        items: [
          'Prepare and deliver accurate quotes',
          'Schedule and coordinate service jobs',
          'Communicate with you about your job status',
          'Document job records for operational and compliance purposes',
          'Fulfill donation authorization and documentation requirements',
          'Maintain disposal, recycling, and facility records as required',
          'Comply with applicable Indiana laws and local regulations',
          'Improve our services and operational processes',
        ],
      },
      {
        type: 'p',
        text: 'We do not use your information for marketing purposes without your explicit consent.',
      },
    ],
  },
  {
    title: '4. Photo Submissions',
    blocks: [
      {
        type: 'p',
        text:
          'Photos submitted through our quote form are used exclusively for job scoping, pricing, and operational planning purposes.',
      },
      { type: 'p', text: 'We will not:' },
      {
        type: 'list',
        items: [
          'Share your photos publicly without your written permission',
          'Use your photos in marketing materials without your consent',
          'Sell or transfer your photos to third parties',
        ],
      },
    ],
  },
  {
    title: '5. Donation Authorization Records',
    blocks: [
      {
        type: 'p',
        text:
          'If you authorize donation or reuse of items from your property, your written authorization is retained as part of the job record. This documentation is maintained for compliance, accountability, and The Hearth Project donation tracking purposes.',
      },
      {
        type: 'p',
        text:
          'We do not assign monetary value to donated items and do not issue tax receipts unless a qualified nonprofit partner independently provides one.',
      },
    ],
  },
  {
    title: '6. Job Documentation and Record Retention',
    blocks: [
      {
        type: 'p',
        text:
          'Job records, including before and after photos, disposal receipts, recycling receipts, donation authorizations, and service documentation, are retained for operational, compliance, and legal purposes in accordance with Indiana business record requirements.',
      },
      {
        type: 'p',
        text:
          'Records are stored securely and accessible only to authorized Ohana personnel and operational partners directly involved in your job.',
      },
    ],
  },
  {
    title: '7. Third-Party Services',
    blocks: [
      {
        type: 'p',
        text:
          'Our website and operations use the following third-party services that may process your submitted information:',
      },
      {
        type: 'subhead',
        text: 'GoHighLevel (GHL)',
      },
      {
        type: 'p',
        text:
          'Used for lead tracking, customer relationship management, and operational coordination. Information submitted through our website may be tracked through GHL systems.',
      },
      { type: 'p', text: 'GHL Privacy Policy: www.gohighlevel.com/privacy-policy' },
      {
        type: 'subhead',
        text: 'Web3Forms',
      },
      {
        type: 'p',
        text:
          'Used to process and forward quote form submissions to our team by email. Submitted information passes through Web3Forms servers for delivery purposes.',
      },
    ],
  },
  {
    title: '8. Data Sharing',
    blocks: [
      {
        type: 'p',
        text:
          'Ohana Property Services does not sell, rent, or share your personal information with third parties except:',
      },
      {
        type: 'list',
        items: [
          'As necessary to fulfill the services you requested',
          'With operational partners directly involved in your job',
          'As required by applicable law, court order, or government authority',
          'With HOL Creative Solutions LLC as the operational support and coordination entity for Ohana Property Services under Orlop Group Holdings',
        ],
      },
      {
        type: 'p',
        text:
          'We do not share your information with advertisers, data brokers, or unrelated third parties.',
      },
    ],
  },
  {
    title: '9. Cookies and Tracking',
    blocks: [
      {
        type: 'p',
        text:
          'Our website uses tracking technology provided by GoHighLevel to understand how visitors interact with our site. This tracking is used for operational improvement and lead management purposes only.',
      },
      {
        type: 'p',
        text:
          'We do not currently use advertising cookies or retargeting pixels. If this changes we will update this Privacy Policy accordingly.',
      },
    ],
  },
  {
    title: '10. Data Security',
    blocks: [
      {
        type: 'p',
        text:
          'We take reasonable precautions to protect your information from unauthorized access, disclosure, alteration, or destruction. These measures include:',
      },
      {
        type: 'list',
        items: [
          'Secure form submission processing through Web3Forms',
          'Encrypted email communications through Google Workspace',
          'Secure database storage through MongoDB',
          'Access controls limiting who can view submitted information',
        ],
      },
      {
        type: 'p',
        text:
          'No method of electronic transmission or storage is 100% secure. We cannot guarantee absolute security but are committed to protecting your information with industry-standard practices.',
      },
    ],
  },
  {
    title: "11. Children's Privacy",
    blocks: [
      {
        type: 'p',
        text:
          'Our services are intended for adults 18 years of age or older. We do not knowingly collect personal information from children under 13. If you believe we have inadvertently collected information from a child please contact us immediately at ohanas@ohanapropertyexperts.com.',
      },
    ],
  },
  {
    title: '12. Your Rights',
    blocks: [
      { type: 'p', text: 'You have the right to:' },
      {
        type: 'list',
        items: [
          'Request access to the personal information we hold about you',
          'Request correction of inaccurate information',
          'Request deletion of your information subject to our legal retention obligations',
          'Opt out of any non-essential communications from us',
        ],
      },
      { type: 'p', text: 'To exercise any of these rights contact us at:' },
      {
        type: 'list',
        items: ['ohanas@ohanapropertyexperts.com', '(812) 302-6833'],
      },
      { type: 'p', text: 'We will respond to all requests within a reasonable timeframe.' },
    ],
  },
  {
    title: '13. Changes to This Privacy Policy',
    blocks: [
      {
        type: 'p',
        text:
          'We may update this Privacy Policy from time to time to reflect changes in our operations, services, or legal requirements. The date at the top of this page indicates when it was last updated. Continued use of our website after any update constitutes acceptance of the revised policy.',
      },
    ],
  },
  {
    title: '14. Contact Us',
    blocks: [
      {
        type: 'p',
        text:
          'For questions, concerns, or requests related to this Privacy Policy contact:',
      },
      {
        type: 'list',
        items: [
          'Ohana Property Services',
          'Ohana Property & Transition Services LLC',
          'Email: ohanas@ohanapropertyexperts.com',
          'Phone: (812) 302-6833',
          'Website: ohanapropertyexperts.com',
        ],
      },
    ],
  },
];

function Block({ block }) {
  if (block.type === 'list') {
    return (
      <ul className="my-5 space-y-2.5">
        {block.items.map((it, i) => (
          <li key={i} className="flex items-start gap-3">
            <span className="mt-1.5 grid h-4 w-4 flex-none place-items-center rounded-full bg-ohana-accent/15 text-ohana-accent">
              <Check className="h-3 w-3" strokeWidth={3} />
            </span>
            <span className="text-base md:text-lg leading-relaxed text-ohana-text">
              {linkify(it)}
            </span>
          </li>
        ))}
      </ul>
    );
  }
  if (block.type === 'subhead') {
    return (
      <h3 className="font-serif text-xl md:text-2xl text-ohana-primary leading-snug mt-7 mb-3">
        {block.text}
      </h3>
    );
  }
  return (
    <p className="text-base md:text-lg leading-relaxed text-ohana-text mb-4">
      {linkify(block.text)}
    </p>
  );
}

export default function Privacy() {
  return (
    <>
      <SeoHead
        title="Privacy Policy | Ohana Property Services"
        description="Privacy Policy for Ohana Property Services covering what we collect, how we use it, third-party services, data sharing, security, your rights, and contact information."
        canonical="https://ohanapropertyexperts.com/privacy"
      />

      <section className="bg-ohana-primary text-ohana-bg">
        <div className="container-ohana pt-24 pb-16 md:pt-32 md:pb-20 max-w-4xl">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-ohana-bg/70 hover:text-white transition mb-8"
            data-testid="privacy-back-link"
          >
            <ArrowLeft className="h-4 w-4" />
            Back Home
          </Link>
          <p className="overline">Legal</p>
          <h1
            className="mt-4 font-serif text-5xl md:text-6xl leading-[1.05]"
            data-testid="privacy-title"
          >
            Privacy Policy
          </h1>
          <p className="mt-5 text-base text-ohana-bg/80 max-w-2xl leading-relaxed">
            Ohana Property Services is a trade name of Ohana Property &amp; Transition Services LLC.
          </p>
          <p className="mt-3 text-sm text-ohana-bg/70">Last updated {LAST_UPDATED}</p>
        </div>
      </section>

      <section className="bg-ohana-bg" data-testid="privacy-body">
        <div className="container-ohana py-16 md:py-24">
          <div className="mx-auto max-w-3xl">
            <div className="space-y-12">
              {SECTIONS.map((s, i) => (
                <div key={i} data-testid={`privacy-section-${i + 1}`}>
                  <h2 className="font-serif text-2xl md:text-3xl text-ohana-primary leading-snug mb-5">
                    {s.title}
                  </h2>
                  {s.blocks.map((b, bi) => (
                    <Block key={bi} block={b} />
                  ))}
                </div>
              ))}
            </div>

            <div className="mt-16 rounded-2xl border border-ohana-border bg-ohana-surface p-6 md:p-8">
              <h3 className="font-serif text-xl text-ohana-primary">Privacy questions?</h3>
              <p className="mt-3 text-ohana-text leading-relaxed">
                Email{' '}
                <a
                  href={`mailto:${BRAND.email}`}
                  className="font-semibold text-ohana-primary underline decoration-ohana-accent decoration-2 underline-offset-4"
                  data-testid="privacy-email-link"
                >
                  {BRAND.email}
                </a>{' '}
                or call{' '}
                <a
                  href={`tel:${BRAND.phoneTel}`}
                  className="font-semibold text-ohana-primary underline decoration-ohana-accent decoration-2 underline-offset-4"
                  data-testid="privacy-phone-link"
                >
                  {BRAND.phone}
                </a>
                .
              </p>
              <p className="mt-5 text-sm text-ohana-text-muted">
                See also our{' '}
                <Link
                  to="/terms"
                  className="font-semibold text-ohana-primary underline decoration-ohana-accent decoration-2 underline-offset-4"
                  data-testid="privacy-to-terms-link"
                >
                  Terms of Service
                </Link>
                .
              </p>
              <p className="mt-6 pt-5 border-t border-ohana-border text-xs text-ohana-text-muted leading-relaxed" data-testid="privacy-legal-disclaimer">
                {BRAND.disclaimer}
              </p>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
